import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import { eventFormAnswersQueryKeys } from "../query-keys";
import { CreateEventFormAnswersForm } from "../schemas";

const createEventFormAnswers = async ({
  data,
  accessToken,
}: {
  data: CreateEventFormAnswersForm;
  // Guest token for cross-site iframe contexts where the session cookie is not sent
  accessToken?: string;
}) => {
  try {
    const { data: response } = await privateAxios.post(
      `/event-form-answers`,
      data,
      accessToken
        ? { headers: { Authorization: `Bearer ${accessToken}` } }
        : undefined
    );
    return response;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useCreateEventFormAnswersMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createEventFormAnswers,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventFormAnswersQueryKeys.all,
      });
    },
  });
};

// Embedded ticketing: the buyer holds no session cross-site, the order-scoped
// token returned by POST /ticketing authorises the answers instead
const createEventFormAnswersByOrder = async ({
  data,
  orderToken,
}: {
  data: CreateEventFormAnswersForm;
  orderToken: string;
}) => {
  try {
    const { data: response } = await privateAxios.post(
      `/event-form-answers/by-order`,
      { ...data, token: orderToken }
    );
    return response;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useCreateEventFormAnswersByOrderMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createEventFormAnswersByOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventFormAnswersQueryKeys.all,
      });
    },
  });
};
